import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Sparkles, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

export default function OnboardingChecklistCard() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { items, completed, total, allDone, isLoading } = useOnboardingChecklist();
  const [dismissing, setDismissing] = useState(false);

  // Si ya fue cerrado manualmente, no renderizar nunca más
  if (profile?.onboarding_checklist_dismissed_at) return null;

  const handleDismiss = async () => {
    if (!user || dismissing) return;
    setDismissing(true);
    try {
      await supabase
        .from('profiles')
        .update({ onboarding_checklist_dismissed_at: new Date().toISOString() })
        .eq('id', user.id);
      await refreshProfile();
    } finally {
      setDismissing(false);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="h-full border-primary/20 bg-gradient-to-br from-card to-primary/5">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">¿Qué debo hacer en la plataforma?</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                {allDone
                  ? '¡Felicidades! Completaste todos los pasos.'
                  : `${total} pasos para sacarle el máximo provecho a la plataforma.`}
              </p>
            </div>
            {allDone && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={handleDismiss}
                    disabled={dismissing}
                    aria-label="Cerrar recorrido"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cerrar recorrido (no volverá a aparecer)</TooltipContent>
              </Tooltip>
            )}
          </div>

          <div className="space-y-1.5">
            <Progress value={(completed / total) * 100} className="h-1.5" />
            <p className="text-xs text-muted-foreground text-right">
              {completed} / {total} completados
            </p>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: total }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <ul className="space-y-1.5">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.key}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => navigate(item.href)}
                          className={cn(
                            'w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition-colors hover:bg-muted/60 group',
                            item.done && 'opacity-70'
                          )}
                        >
                          {item.done ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                          ) : (
                            <Circle className="h-5 w-5 text-muted-foreground shrink-0 group-hover:text-primary transition-colors" />
                          )}
                          <div className="flex items-center gap-1.5 flex-1 min-w-0">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span
                              className={cn(
                                'text-sm font-medium truncate',
                                item.done && 'line-through text-muted-foreground'
                              )}
                            >
                              {item.label}
                            </span>
                          </div>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs">
                        <p className="font-semibold mb-1">{item.label}</p>
                        <p className="text-xs opacity-90">{item.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
